import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Icon,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { coloresEmma, obtenerValores } from "../../../global/js/funciones";
import TareasTerminadas from "../../general/TareasTerminadas";
import emma_s1 from "../../../global/imagenes/emma_s1.png";
import emma_w from "../../../global/imagenes/emma_w.png";
import { useStyles } from "../styles";
import { GENERICA_PROGRAMAS, SEMESTRES, SECCIONES } from "../helper";
import { obtenerAnalisisResumen, obtenerAnalisisSeccion } from "../services";
import NubePalabras from "./NubePalabras";

// Pestaña "Análisis de información": muestra tendencias generales del proyecto de vida.
export default function AnalisisInformacion({ actualizarMensaje }) {
  const classes = useStyles();

  // Filtros
  const [opcionesProgramas, setOpcionesProgramas] = useState([]);
  const [programa, setPrograma] = useState(null);
  const [semestre, setSemestre] = useState("");

  // Resultado del análisis
  const [generado, setGenerado] = useState(false);
  const [cargandoResumen, setCargandoResumen] = useState(false);
  const [resumen, setResumen] = useState(null); // { totalEstudiantes, porcentaje }
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [seccionActiva, setSeccionActiva] = useState(SECCIONES[0].key);
  const [seccionesData, setSeccionesData] = useState({}); // caché por sección
  const [cargandoSeccion, setCargandoSeccion] = useState(false);

  // Sin programa no se habilita ni el semestre ni el botón de generar
  const generarHabilitado = !!programa;

  // Cargar programas (genérica 5)
  useEffect(() => {
    let activo = true;
    (async () => {
      const data = await obtenerValores(GENERICA_PROGRAMAS);
      if (activo && Array.isArray(data)) {
        setOpcionesProgramas(data.map((p) => ({ value: p.id, label: p.nombre })));
      }
    })();
    return () => {
      activo = false;
    };
  }, []);

  // Carga perezosa del análisis de una sección (con caché). Recibe los filtros
  // aplicados en "Generar Análisis" para no depender de lo que se edite después.
  const cargarSeccion = async (key, filtros, cache) => {
    if (cache && cache[key]) return; // ya está en caché
    setCargandoSeccion(true);
    const { estado, resp } = await obtenerAnalisisSeccion(key, filtros);
    if (estado === 200 && resp) {
      setSeccionesData((prev) => ({ ...prev, [key]: resp }));
    } else if (actualizarMensaje) {
      actualizarMensaje({ titulo: "No se pudo cargar el análisis de la sección.", tipo: "error", mostrar: true, tiempo: 6000 });
    }
    setCargandoSeccion(false);
  };

  const generarAnalisis = async () => {
    const filtros = { programa: programa.value, semestre: semestre || "" };
    setCargandoResumen(true);
    setGenerado(true);
    setResumen(null);
    setSeccionesData({});
    setFiltrosAplicados(filtros);
    const primera = SECCIONES[0].key;
    setSeccionActiva(primera);

    const { estado, resp } = await obtenerAnalisisResumen(filtros);
    if (estado === 200 && resp) {
      setResumen(resp);
      setCargandoResumen(false);
      // Cargar de una vez la primera sección
      cargarSeccion(primera, filtros, {});
    } else {
      setCargandoResumen(false);
      setGenerado(false);
      if (actualizarMensaje) {
        actualizarMensaje({ titulo: "No se pudo generar el análisis, intenta más tarde.", tipo: "error", mostrar: true, tiempo: 6000 });
      }
    }
  };

  const seleccionarSeccion = (key) => {
    setSeccionActiva(key);
    cargarSeccion(key, filtrosAplicados, seccionesData);
  };

  const limpiar = () => {
    setPrograma(null);
    setSemestre("");
    setGenerado(false);
    setResumen(null);
    setSeccionesData({});
    setFiltrosAplicados({});
  };

  const seccion = SECCIONES.find((s) => s.key === seccionActiva) || SECCIONES[0];
  const datosSeccion = seccionesData[seccionActiva] || null;
  const cargandoSeccionActual = cargandoSeccion && !datosSeccion;

  return (
    <div style={{ padding: "20px 28px 60px 28px" }}>
      <Typography variant="h6" style={{ fontWeight: 700, color: "#2f2f2f" }}>
        Análisis de Proyecto de Vida
      </Typography>
      <Typography variant="body2" style={{ color: "#6b6b6b", marginBottom: 20 }}>
        Explora las principales tendencias y palabras frecuentes identificadas en las respuestas de los estudiantes.
      </Typography>

      {/* Filtros */}
      <div className={classes.filtrosCard}>
        <div className={classes.filtrosRow}>
          <FormControl className="form-control" style={{ flex: 1, minWidth: 220 }}>
            <Autocomplete
              options={opcionesProgramas}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              value={programa}
              onChange={(event, newValue) => {
                setPrograma(newValue);
                if (!newValue) setSemestre("");
              }}
              renderInput={(params) => <TextField {...params} label="Programa académico" />}
              noOptionsText="Sin resultados"
            />
          </FormControl>

          <FormControl className="form-control" style={{ flex: 1, minWidth: 200 }} disabled={!programa}>
            <InputLabel>Semestre</InputLabel>
            <Select
              value={semestre}
              onChange={(e) => setSemestre(e.target.value)}
              endAdornment={
                semestre !== "" && programa ? (
                  <IconButton
                    size="small"
                    style={{ marginRight: 18, padding: 2 }}
                    onClick={() => setSemestre("")}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Icon fontSize="small">close</Icon>
                  </IconButton>
                ) : null
              }
            >
              {SEMESTRES.map((s) => (
                <MenuItem key={s} value={s}>
                  Semestre {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div className={classes.filtrosAcciones}>
            <Button
              variant="outlined"
              onClick={limpiar}
              startIcon={<Icon>filter_alt</Icon>}
              className={classes.btnCancelar}
            >
              Limpiar filtros
            </Button>
            <Button
              variant="contained"
              onClick={generarAnalisis}
              disabled={!generarHabilitado || cargandoResumen}
              startIcon={<Icon>bar_chart</Icon>}
              className={classes.btnPrimario}
            >
              Generar Análisis
            </Button>
          </div>
        </div>
      </div>

      {/* Resultado */}
      {cargandoResumen ? (
        <TareasTerminadas mensaje="Generando análisis.." marginTop="7%" imagen={emma_w} widthImg="7%" cargando={true} />
      ) : generado && resumen ? (
        <>
          {/* Cards de resumen */}
          <div className={classes.analisisCardsRow}>
            {/* 1. Estudiantes analizados */}
            <div className={classes.analisisCard}>
              <div
                className={classes.analisisCardIcon}
                style={{ backgroundColor: "#fdecec", color: coloresEmma.primarycolor }}
              >
                <Icon style={{ fontSize: 30 }}>people</Icon>
              </div>
              <div>
                <Typography variant="caption" style={{ color: "#888" }}>
                  Estudiantes analizados
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 800, color: "#2f2f2f", lineHeight: 1.1 }}>
                  {(resumen.totalEstudiantes || 0).toLocaleString("es-CO")}
                </Typography>
                <Typography variant="caption" style={{ color: "#2e9e5b", fontWeight: 700 }}>
                  {resumen.porcentaje || 0}% del total del programa
                </Typography>
              </div>
            </div>

            {/* 2. Palabra más frecuente (según el aspecto seleccionado) */}
            <div className={classes.analisisCard}>
              <div className={classes.analisisCardIcon} style={{ backgroundColor: "#e8f5ec", color: "#2e9e5b" }}>
                <span style={{ fontSize: 24, fontWeight: 800 }}>Aa</span>
              </div>
              <div>
                <Typography variant="caption" style={{ color: "#888" }}>
                  Palabra más frecuente
                </Typography>
                <Typography variant="h6" style={{ fontWeight: 800, color: "#2f2f2f", lineHeight: 1.15 }}>
                  {cargandoSeccionActual ? "…" : (datosSeccion && datosSeccion.palabraFrecuente) || "—"}
                </Typography>
              </div>
            </div>

            {/* 3. Total de palabras analizadas (según el aspecto seleccionado) */}
            <div className={classes.analisisCard}>
              <div className={classes.analisisCardIcon} style={{ backgroundColor: "#efe9f7", color: "#7E57C2" }}>
                <span style={{ fontSize: 26, fontWeight: 800 }}>#</span>
              </div>
              <div>
                <Typography variant="caption" style={{ color: "#888" }}>
                  Total de palabras analizadas
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 800, color: "#2f2f2f", lineHeight: 1.1 }}>
                  {cargandoSeccionActual ? "…" : (datosSeccion ? (datosSeccion.totalPalabras || 0).toLocaleString("es-CO") : "—")}
                </Typography>
              </div>
            </div>
          </div>

          {/* Card de nube de palabras */}
          <div className={classes.nubeCard}>
            {/* Pestañas por aspecto (sin la frase) */}
            <div style={{ display: "flex", flexWrap: "wrap", borderBottom: "1px solid #eee" }}>
              {SECCIONES.map((s) => {
                const activo = s.key === seccionActiva;
                return (
                  <div
                    key={s.key}
                    className={classes.nubeTab}
                    style={activo ? { color: coloresEmma.primarycolor, borderBottomColor: coloresEmma.primarycolor } : {}}
                    onClick={() => seleccionarSeccion(s.key)}
                  >
                    <img src={s.logo} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} />
                    {s.titulo}
                  </div>
                );
              })}
            </div>

            {/* Área de la nube de palabras del aspecto seleccionado */}
            <div className={classes.nubeArea}>
              {cargandoSeccionActual ? (
                <TareasTerminadas mensaje="Cargando.." marginTop="0" imagen={emma_w} widthImg="6%" cargando={true} />
              ) : datosSeccion && datosSeccion.nube && datosSeccion.nube.length ? (
                <NubePalabras palabras={datosSeccion.nube} />
              ) : (
                <>
                  <img src={emma_s1} alt="Emma" style={{ width: "9%", minWidth: 90, opacity: 0.9 }} />
                  <Typography variant="body2" style={{ color: "#9e9e9e", marginTop: 12, textAlign: "center" }}>
                    No hay información suficiente para generar la nube de {seccion.titulo.toLowerCase()}.
                  </Typography>
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <TareasTerminadas
          mensaje="Selecciona un programa y genera el análisis para ver los resultados"
          marginTop="7%"
          imagen={emma_s1}
          widthImg="7%"
        />
      )}
    </div>
  );
}
